import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { type UploadClaimCodesBody, uploadClaimCodes } from '~/services/LuxLoyalty';

import { claimCampaignQueryKeys } from './claimCampaignQueryKeys';

interface UploadClaimCodesParams {
  campaignId: string;
  body: UploadClaimCodesBody;
}

/**
 * Uploads codes to a claim campaign and invalidates the codes cache.
 */
export function useUploadClaimCodes() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ campaignId, body }: UploadClaimCodesParams) => {
      return await uploadClaimCodes(campaignId, body);
    },
    onSuccess: (response, { campaignId }) => {
      queryClient.invalidateQueries({ queryKey: claimCampaignQueryKeys.codes(campaignId) });
      const { createdCount, duplicatesSkipped } = response.result;
      enqueueSnackbar(`Codes uploaded: ${createdCount} created, ${duplicatesSkipped} duplicates skipped`, {
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error uploading codes: ${error.message || 'Unknown error'}`, { variant: 'error' });
    },
  });
}
